import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams } from "react-router";

import { DataTable } from "../../components/DataTable";
import { ConfirmDialog, Dialog } from "../../components/Dialog";
import {
  Alert,
  Badge,
  Button,
  Card,
  LoadingBlock,
  PageHeader,
  SelectField,
  TextField,
} from "../../components/ui";
import { useToast } from "../../components/Toast";
import { api, errorMessage } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useConfig } from "../../lib/config";
import { formatBytes, formatDate, formatDateTime } from "../../lib/format";
import type { Page } from "../../lib/types";

function useTz() {
  return useConfig().data?.business_timezone ?? "UTC";
}

// --- Accounts list with filters --------------------------------------------------------------

interface UserRow {
  id: string;
  name: string;
  email: string;
  status: "active" | "disabled";
  verified: boolean;
  roles: string[];
  companies: string[];
  order_count: number;
  created_at: string;
  last_login_at: string | null;
}

const FILTERS = {
  role: [
    { value: "customer", label: "Customers" },
    { value: "staff", label: "All staff" },
    { value: "employee", label: "Employees" },
    { value: "administrator", label: "Administrators" },
  ],
  status: [
    { value: "active", label: "Active" },
    { value: "disabled", label: "Disabled" },
  ],
  verified: [
    { value: "true", label: "Email confirmed" },
    { value: "false", label: "Email not confirmed" },
  ],
  mfa: [
    { value: "true", label: "Two-step on" },
    { value: "false", label: "Two-step off" },
  ],
};

export function AdminUsersPage() {
  const tz = useTz();
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({ role: "", status: "", verified: "", mfa: "" });
  const [page, setPage] = useState(1);
  const set = (k: keyof typeof filters, v: string) => {
    setPage(1);
    setFilters((f) => ({ ...f, [k]: v }));
  };
  const data = useQuery({
    queryKey: ["admin-users", q, filters, page],
    queryFn: () => {
      const p = new URLSearchParams({ q, page: String(page) });
      Object.entries(filters).forEach(([k, v]) => v && p.set(k, v));
      return api.get<Page<UserRow>>(`/admin/users?${p}`);
    },
  });
  const active = Object.values(filters).some(Boolean) || q;
  return (
    <div>
      <PageHeader
        title="Accounts"
        description="Every person with a sign-in. Select a name to view or edit their details."
      />
      <div className="mb-4 grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <TextField
          label="Search"
          placeholder="Name or email"
          value={q}
          onChange={(e) => {
            setPage(1);
            setQ(e.target.value);
          }}
        />
        <SelectField
          label="Type"
          placeholder="Everyone"
          options={FILTERS.role}
          value={filters.role}
          onChange={(e) => set("role", e.target.value)}
        />
        <SelectField
          label="Status"
          placeholder="Any"
          options={FILTERS.status}
          value={filters.status}
          onChange={(e) => set("status", e.target.value)}
        />
        <SelectField
          label="Email"
          placeholder="Any"
          options={FILTERS.verified}
          value={filters.verified}
          onChange={(e) => set("verified", e.target.value)}
        />
        <SelectField
          label="Two-step"
          placeholder="Any"
          options={FILTERS.mfa}
          value={filters.mfa}
          onChange={(e) => set("mfa", e.target.value)}
        />
      </div>
      {active && (
        <Button
          variant="ghost"
          className="mb-3"
          onClick={() => {
            setQ("");
            setPage(1);
            setFilters({ role: "", status: "", verified: "", mfa: "" });
          }}
        >
          Clear filters
        </Button>
      )}
      <DataTable
        caption="Accounts"
        loading={data.isLoading}
        rows={data.data?.items}
        rowKey={(r) => r.id}
        page={page}
        pageSize={25}
        total={data.data?.total}
        onPage={setPage}
        emptyTitle="No accounts match these filters"
        columns={[
          {
            key: "name",
            header: "Name",
            render: (r) => (
              <span>
                <Link
                  className="font-semibold text-brand-700 underline"
                  to={`/admin/users/${r.id}`}
                >
                  {r.name}
                </Link>
                <span className="block text-ink-muted">{r.email}</span>
              </span>
            ),
          },
          { key: "orders", header: "Orders", render: (r) => r.order_count },
          {
            key: "flags",
            header: "Status",
            render: (r) => (
              <span className="flex flex-wrap gap-1">
                <Badge tone={r.status === "active" ? "ok" : "danger"}>{r.status}</Badge>
                {!r.verified && <Badge tone="warn">unconfirmed</Badge>}
                {r.roles.length === 0 ? (
                  <Badge>customer</Badge>
                ) : (
                  r.roles.map((x) => (
                    <Badge key={x} tone="brand">
                      {x}
                    </Badge>
                  ))
                )}
              </span>
            ),
          },
          { key: "created", header: "Joined", render: (r) => formatDate(r.created_at, tz) },
          {
            key: "login",
            header: "Last sign-in",
            render: (r) => formatDateTime(r.last_login_at, tz) || "Never",
          },
        ]}
      />
    </div>
  );
}

// --- Account detail / edit -------------------------------------------------------------------

interface UserDetail {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone: string | null;
  status: "active" | "disabled";
  verified: boolean;
  roles: ("employee" | "administrator")[];
  mfa_enabled: boolean;
  created_at: string;
  last_login_at: string | null;
  customer_name: string | null;
  company_name: string | null;
  contact_email: string | null;
  street: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  default_delivery_method: string | null;
  default_payment_method: string | null;
  memberships: { company_id: string; company_name: string; role: string; status: string }[];
}

const EDIT_FIELDS: {
  key: keyof UserDetail;
  label: string;
  type?: string;
  wide?: boolean;
  customerOnly?: boolean;
}[] = [
  { key: "first_name", label: "First name" },
  { key: "last_name", label: "Last name" },
  { key: "email", label: "Sign-in email", type: "email", wide: true },
  { key: "phone", label: "Phone", type: "tel" },
  { key: "customer_name", label: "Name on orders", customerOnly: true },
  { key: "company_name", label: "Company name (shown on orders)", customerOnly: true },
  { key: "contact_email", label: "Contact email for orders", type: "email", customerOnly: true },
  { key: "street", label: "Street address", wide: true, customerOnly: true },
  { key: "city", label: "City", customerOnly: true },
  { key: "state", label: "State", customerOnly: true },
  { key: "zip_code", label: "ZIP", customerOnly: true },
];

export function AdminUserPage() {
  const { id } = useParams();
  const tz = useTz();
  const { user: me } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["admin-user", id],
    queryFn: () => api.get<UserDetail>(`/admin/users/${id}`),
  });
  const [confirm, setConfirm] = useState<"mfa" | "status" | null>(null);

  if (q.isLoading) return <LoadingBlock />;
  if (!q.data) return <Alert tone="error">{errorMessage(q.error)}</Alert>;
  const u = q.data;
  const isStaff = u.roles.length > 0;
  const isSelf = me?.id === u.id;
  const refresh = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ["admin-user", id] }),
      qc.invalidateQueries({ queryKey: ["admin-users"] }),
      qc.invalidateQueries({ queryKey: ["admin-staff"] }),
    ]);
  const act = async (fn: () => Promise<unknown>, msg: string) => {
    try {
      await fn();
      notify(msg);
      await refresh();
    } catch (err) {
      notify(errorMessage(err), "error");
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={`${u.first_name} ${u.last_name}`}
        description={
          <span className="flex flex-wrap items-center gap-2">
            {u.email}
            <Badge tone={u.status === "active" ? "ok" : "danger"}>{u.status}</Badge>
            {isStaff ? (
              u.roles.map((r) => (
                <Badge key={r} tone="brand">
                  {r}
                </Badge>
              ))
            ) : (
              <Badge>customer</Badge>
            )}
            {!u.verified && <Badge tone="warn">email not confirmed</Badge>}
          </span>
        }
        actions={
          <Link
            to={isStaff ? "/admin" : "/admin/users"}
            className="inline-flex min-h-11 items-center px-3 text-brand-700 underline"
          >
            Back to {isStaff ? "employees" : "accounts"}
          </Link>
        }
      />
      <UserDetailsForm key={JSON.stringify(u)} user={u} onSaved={refresh} />

      <Card>
        <h2 className="mb-3 text-lg font-bold">Sign-in and access</h2>
        <dl className="mb-4 grid gap-2 sm:grid-cols-3 text-sm">
          <div>
            <dt className="text-ink-muted">Two-step verification</dt>
            <dd>{u.mfa_enabled ? "On" : "Not set up"}</dd>
          </div>
          <div>
            <dt className="text-ink-muted">Last sign-in</dt>
            <dd>{formatDateTime(u.last_login_at, tz) || "Never"}</dd>
          </div>
          <div>
            <dt className="text-ink-muted">Joined</dt>
            <dd>{formatDate(u.created_at, tz)}</dd>
          </div>
        </dl>
        {isSelf ? (
          <p className="text-sm text-ink-muted">You cannot change your own access here.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={() => setConfirm("mfa")}>
              Force two-step reset
            </Button>
            <Button
              variant={u.status === "active" ? "danger" : "primary"}
              onClick={() => setConfirm("status")}
            >
              {u.status === "active" ? "Disable account" : "Enable account"}
            </Button>
            {isStaff && (
              <>
                <Button
                  variant="ghost"
                  onClick={() =>
                    act(
                      () =>
                        api.put(`/admin/users/${u.id}/roles`, {
                          roles: u.roles.includes("administrator")
                            ? ["employee"]
                            : ["employee", "administrator"],
                        }),
                      "Roles updated. They must sign in again.",
                    )
                  }
                >
                  {u.roles.includes("administrator") ? "Make employee only" : "Make administrator"}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() =>
                    act(
                      () => api.post(`/admin/users/${u.id}/resend-setup`),
                      "Password setup link sent.",
                    )
                  }
                >
                  Send password setup link
                </Button>
              </>
            )}
          </div>
        )}
      </Card>

      <ConfirmDialog
        open={confirm === "mfa"}
        title="Force a two-step verification reset?"
        body="They will be signed out everywhere and must set up two-step verification again (with a new QR code) the next time they sign in."
        confirmLabel="Force reset"
        danger
        onCancel={() => setConfirm(null)}
        onConfirm={async () => {
          setConfirm(null);
          await act(() => api.post(`/admin/users/${u.id}/reset-mfa`), "Two-step reset forced.");
        }}
      />
      <ConfirmDialog
        open={confirm === "status"}
        title={u.status === "active" ? "Disable this account?" : "Enable this account?"}
        body={
          u.status === "active"
            ? "They will be signed out everywhere and cannot sign in until enabled again."
            : "They will be able to sign in again."
        }
        confirmLabel={u.status === "active" ? "Disable" : "Enable"}
        danger={u.status === "active"}
        onCancel={() => setConfirm(null)}
        onConfirm={async () => {
          setConfirm(null);
          await act(
            () =>
              api.put(`/admin/users/${u.id}/status`, {
                status: u.status === "active" ? "disabled" : "active",
              }),
            "Account updated.",
          );
        }}
      />
    </div>
  );
}

/** Starts from the loaded account; remounts (via key) when the saved data changes. */
function UserDetailsForm({
  user: u,
  onSaved,
}: {
  user: UserDetail;
  onSaved: () => Promise<unknown>;
}) {
  const { data: config } = useConfig();
  const { notify } = useToast();
  const isStaff = u.roles.length > 0;
  const [form, setForm] = useState<Record<string, string>>(() => {
    const f: Record<string, string> = {};
    for (const k of [
      ...EDIT_FIELDS.map((x) => x.key),
      "default_delivery_method",
      "default_payment_method",
    ])
      f[k] = (u[k as keyof UserDetail] as string | null) ?? "";
    return f;
  });
  const [saving, setSaving] = useState(false);
  const changes = Object.fromEntries(
    Object.entries(form).filter(
      ([k, v]) => v !== ((u[k as keyof UserDetail] as string | null) ?? ""),
    ),
  );
  return (
    <Card>
      <h2 className="mb-3 text-lg font-bold">Details</h2>
      <form
        className="grid gap-3 sm:grid-cols-2"
        onSubmit={async (e) => {
          e.preventDefault();
          setSaving(true);
          try {
            await api.patch(`/admin/users/${u.id}`, changes);
            notify(
              changes.email
                ? "Saved. The old email address was notified and they must sign in again."
                : "Saved.",
            );
            await onSaved();
          } catch (err) {
            notify(errorMessage(err), "error");
          } finally {
            setSaving(false);
          }
        }}
      >
        {EDIT_FIELDS.filter((f) => !(isStaff && f.customerOnly)).map((f) => (
          <div key={f.key} className={f.wide ? "sm:col-span-2" : undefined}>
            <TextField
              label={f.label}
              type={f.type ?? "text"}
              value={form[f.key] ?? ""}
              hint={
                f.key === "email"
                  ? "Changing this changes how they sign in. Their old address is notified."
                  : undefined
              }
              onChange={(e) => setForm((x) => ({ ...x, [f.key]: e.target.value }))}
            />
          </div>
        ))}
        {!isStaff && config && (
          <>
            <SelectField
              label="Usual delivery"
              options={config.options.delivery_methods}
              value={form.default_delivery_method ?? ""}
              onChange={(e) => setForm((x) => ({ ...x, default_delivery_method: e.target.value }))}
            />
            <SelectField
              label="Usual payment"
              options={config.options.payment_methods}
              value={form.default_payment_method ?? ""}
              onChange={(e) => setForm((x) => ({ ...x, default_payment_method: e.target.value }))}
            />
          </>
        )}
        <div className="sm:col-span-2">
          <Button type="submit" busy={saving} disabled={Object.keys(changes).length === 0}>
            Save changes
          </Button>
        </div>
      </form>
    </Card>
  );
}

// --- File manager ----------------------------------------------------------------------------

interface FileRow {
  kind: "order" | "delivery";
  id: string;
  name: string;
  size_bytes: number;
  owner: string;
  reference: string;
  reference_id: string;
  uploaded_at: string;
  expires_at: string | null;
  deletion_status: "active" | "deleted" | "failed";
  deleted_at: string | null;
}

export function AdminFilesPage() {
  const tz = useTz();
  const { notify } = useToast();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("");
  const [status, setStatus] = useState("active");
  const [page, setPage] = useState(1);
  const [target, setTarget] = useState<FileRow | null>(null);
  const [reason, setReason] = useState("");
  const [orphan, setOrphan] = useState<string | null>(null);
  const files = useQuery({
    queryKey: ["admin-files", q, kind, status, page],
    queryFn: () => {
      const p = new URLSearchParams({ q, page: String(page), page_size: "50" });
      if (kind) p.set("kind", kind);
      if (status) p.set("status", status);
      return api.get<Page<FileRow>>(`/admin/files?${p}`);
    },
  });
  const orphans = useQuery({
    queryKey: ["admin-orphans"],
    queryFn: () =>
      api.get<{
        items: { key: string; size_bytes: number; modified: string }[];
        total_bytes: number;
      }>("/admin/files/unreferenced"),
  });
  const refresh = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ["admin-files"] }),
      qc.invalidateQueries({ queryKey: ["admin-orphans"] }),
      qc.invalidateQueries({ queryKey: ["admin-storage"] }),
    ]);

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="Files"
        description="Every customer print file and every file Bill's has sent. Deleting a file removes it from the server immediately; customers can no longer download it."
      />
      <div className="grid gap-3 sm:grid-cols-3">
        <TextField
          label="Search"
          placeholder="File name, order number, customer or package"
          value={q}
          onChange={(e) => {
            setPage(1);
            setQ(e.target.value);
          }}
        />
        <SelectField
          label="Type"
          placeholder="All files"
          options={[
            { value: "order", label: "Customer print files" },
            { value: "delivery", label: "Files FROM Bill's" },
          ]}
          value={kind}
          onChange={(e) => {
            setPage(1);
            setKind(e.target.value);
          }}
        />
        <SelectField
          label="Status"
          placeholder="Any"
          options={[
            { value: "active", label: "On the server" },
            { value: "deleted", label: "Deleted" },
          ]}
          value={status}
          onChange={(e) => {
            setPage(1);
            setStatus(e.target.value);
          }}
        />
      </div>
      <DataTable
        caption="Stored files"
        loading={files.isLoading}
        error={files.error ? errorMessage(files.error) : null}
        rows={files.data?.items}
        rowKey={(f) => `${f.kind}-${f.id}`}
        page={page}
        pageSize={50}
        total={files.data?.total}
        onPage={setPage}
        emptyTitle="No files match"
        columns={[
          {
            key: "name",
            header: "File",
            render: (f) => (
              <span>
                <span className="font-medium">{f.name}</span>
                <span className="block text-ink-muted">{formatBytes(f.size_bytes)}</span>
              </span>
            ),
          },
          {
            key: "type",
            header: "Type",
            render: (f) => (
              <Badge tone={f.kind === "order" ? "neutral" : "brand"}>
                {f.kind === "order" ? "Customer file" : "From Bill's"}
              </Badge>
            ),
          },
          {
            key: "ref",
            header: "Belongs to",
            render: (f) => (
              <span>
                <Link
                  className="text-brand-700 underline"
                  to={
                    f.kind === "order"
                      ? `/staff/orders/${f.reference_id}`
                      : `/staff/files/${f.reference_id}`
                  }
                >
                  {f.reference}
                </Link>
                <span className="block text-ink-muted">{f.owner}</span>
              </span>
            ),
          },
          { key: "uploaded", header: "Added", render: (f) => formatDate(f.uploaded_at, tz) },
          {
            key: "expires",
            header: "Removed on",
            render: (f) =>
              f.deleted_at
                ? `Deleted ${formatDate(f.deleted_at, tz)}`
                : formatDate(f.expires_at, tz) || "—",
          },
          {
            key: "act",
            header: "",
            render: (f) =>
              f.deletion_status === "deleted" ? null : (
                <span className="flex gap-1">
                  <a
                    className="inline-flex min-h-11 items-center px-2 text-sm font-semibold text-brand-700 underline"
                    href={`/api/v1/admin/files/${f.kind}/${f.id}/download`}
                  >
                    Download
                  </a>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setReason("");
                      setTarget(f);
                    }}
                  >
                    Delete
                  </Button>
                </span>
              ),
          },
        ]}
      />

      <Card>
        <h2 className="text-lg font-bold">Unreferenced files on disk</h2>
        <p className="mt-1 text-sm text-ink-muted">
          Files on the server that no order or package uses, such as leftovers from interrupted
          uploads. Files changed in the last hour are not shown.
        </p>
        {orphans.data && orphans.data.items.length === 0 && (
          <p className="mt-3">None. The server is tidy.</p>
        )}
        {orphans.data && orphans.data.items.length > 0 && (
          <>
            <p className="mt-3 font-semibold">
              {orphans.data.items.length} file(s), {formatBytes(orphans.data.total_bytes)}
            </p>
            <ul className="mt-2 divide-y divide-line text-sm">
              {orphans.data.items.map((o) => (
                <li key={o.key} className="flex flex-wrap items-center justify-between gap-2 py-2">
                  <span>
                    <code>{o.key}</code> · {formatBytes(o.size_bytes)} ·{" "}
                    {formatDateTime(o.modified, tz)}
                  </span>
                  <Button variant="ghost" onClick={() => setOrphan(o.key)}>
                    Delete
                  </Button>
                </li>
              ))}
            </ul>
          </>
        )}
      </Card>

      <Dialog
        open={!!target}
        title={`Delete ${target?.name ?? "file"}?`}
        onClose={() => setTarget(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setTarget(null)}>
              Go back
            </Button>
            <Button
              variant="danger"
              disabled={reason.trim().length < 3}
              onClick={async () => {
                const f = target!;
                setTarget(null);
                try {
                  await api.post(`/admin/files/${f.kind}/${f.id}/delete`, { reason });
                  notify("File deleted.");
                  await refresh();
                } catch (err) {
                  notify(errorMessage(err), "error");
                }
              }}
            >
              Delete file
            </Button>
          </>
        }
      >
        <p className="mb-3">
          This permanently removes the file from the server (
          {target && formatBytes(target.size_bytes)}). The record that it existed is kept.
        </p>
        <TextField
          label="Reason"
          required
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </Dialog>
      <ConfirmDialog
        open={!!orphan}
        title="Delete this unreferenced file?"
        body={<code className="break-all">{orphan}</code>}
        confirmLabel="Delete"
        danger
        onCancel={() => setOrphan(null)}
        onConfirm={async () => {
          const key = orphan!;
          setOrphan(null);
          try {
            await api.post("/admin/files/unreferenced/delete", { key });
            notify("File deleted.");
            await refresh();
          } catch (err) {
            notify(errorMessage(err), "error");
          }
        }}
      />
    </div>
  );
}

// --- Company details form ----------------------------------------------------------------------

interface CompanyEditable {
  display_name: string;
  legal_name: string | null;
  billing_account_ref: string | null;
  phone: string | null;
  street: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  notification_emails: string[];
}

const COMPANY_FIELDS: { key: keyof CompanyEditable; label: string; wide?: boolean }[] = [
  { key: "display_name", label: "Company name" },
  { key: "legal_name", label: "Legal name" },
  { key: "billing_account_ref", label: "Bill's Blue account reference" },
  { key: "phone", label: "Phone" },
  { key: "street", label: "Street address", wide: true },
  { key: "city", label: "City" },
  { key: "state", label: "State" },
  { key: "zip_code", label: "ZIP" },
];

export function CompanyEditForm({ companyId }: { companyId: string }) {
  const q = useQuery({
    queryKey: ["admin-company", companyId],
    queryFn: () => api.get<CompanyEditable>(`/admin/companies/${companyId}`),
  });
  if (!q.data) return null;
  return <CompanyFields key={JSON.stringify(q.data)} companyId={companyId} data={q.data} />;
}

function CompanyFields({ companyId, data }: { companyId: string; data: CompanyEditable }) {
  const { notify } = useToast();
  const qc = useQueryClient();
  const [form, setForm] = useState<Record<string, string>>(() => {
    const f: Record<string, string> = {};
    for (const { key } of COMPANY_FIELDS) f[key] = (data[key] as string | null) ?? "";
    f.notification_emails = data.notification_emails.join("\n");
    return f;
  });
  const [saving, setSaving] = useState(false);
  const original = (k: string) =>
    k === "notification_emails"
      ? data.notification_emails.join("\n")
      : ((data[k as keyof CompanyEditable] as string | null) ?? "");
  const changed = Object.keys(form).filter((k) => form[k] !== original(k));
  return (
    <Card>
      <h2 className="mb-3 font-bold">Company details</h2>
      <form
        className="grid gap-3 sm:grid-cols-2"
        onSubmit={async (e) => {
          e.preventDefault();
          const body: Record<string, unknown> = {};
          for (const k of changed)
            body[k] =
              k === "notification_emails"
                ? form[k]!.split(/[\n,]/)
                    .map((x) => x.trim())
                    .filter(Boolean)
                : form[k] || null;
          setSaving(true);
          try {
            await api.patch(`/admin/companies/${companyId}`, body);
            notify("Company details saved.");
            await qc.invalidateQueries({ queryKey: ["admin-company", companyId] });
          } catch (err) {
            notify(errorMessage(err), "error");
          } finally {
            setSaving(false);
          }
        }}
      >
        {COMPANY_FIELDS.map((f) => (
          <div key={f.key} className={f.wide ? "sm:col-span-2" : undefined}>
            <TextField
              label={f.label}
              required={f.key === "display_name"}
              value={form[f.key] ?? ""}
              onChange={(e) => setForm((x) => ({ ...x, [f.key]: e.target.value }))}
            />
          </div>
        ))}
        <div className="sm:col-span-2">
          <label className="flex flex-col gap-1 text-sm font-semibold" htmlFor="company-recipients">
            Extra email recipients for order emails (one per line)
            <textarea
              id="company-recipients"
              rows={3}
              className="min-h-11 w-full rounded-md border border-line bg-white px-3 py-2 text-base font-normal"
              value={form.notification_emails ?? ""}
              onChange={(e) => setForm((x) => ({ ...x, notification_emails: e.target.value }))}
            />
          </label>
        </div>
        <div className="sm:col-span-2">
          <Button type="submit" busy={saving} disabled={changed.length === 0}>
            Save company details
          </Button>
        </div>
      </form>
    </Card>
  );
}
